import { useRef, useState } from 'react'
import { ChevronLeftIcon, ChevronRightIcon, PlusIcon, RefreshCwIcon, StarIcon, Trash2Icon } from 'lucide-react'
import { ConfirmDialog, Spinner } from '@rentflowdev/ui'
import { useTranslation } from 'react-i18next'
import {
	useAddPropertyImageMutation,
	useRemovePropertyImageMutation,
	useReorderPropertyImagesMutation,
	useGenerateDocumentUploadUrlMutation,
} from '@/api'
import type { PropertyImage } from '@/types'

interface PropertyPhotosSectionProps {
	propertyId: string
	images: PropertyImage[]
}

export function PropertyPhotosSection({ propertyId, images }: PropertyPhotosSectionProps) {
	const { t } = useTranslation()
	const fileInputRef = useRef<HTMLInputElement>(null)
	// Which image the picked file replaces; null means "append a new one".
	const replacingIdRef = useRef<string | null>(null)
	const [busy, setBusy] = useState(false)
	const [error, setError] = useState<string | null>(null)

	const [addImage]      = useAddPropertyImageMutation()
	const [removeImage]   = useRemovePropertyImageMutation()
	// Фотография объекта удаляется с сервера сразу, без отмены — спрашиваем.
	const [deleteTargetId, setDeleteTargetId] = useState<string | null>(null)
	const [reorderImages] = useReorderPropertyImagesMutation()
	const [generateUrl]   = useGenerateDocumentUploadUrlMutation()

	const sorted = [...images].sort((a, b) => a.order - b.order)

	function openPicker(replacingId: string | null) {
		replacingIdRef.current = replacingId
		fileInputRef.current?.click()
	}

	async function uploadFile(file: File): Promise<string> {
		const { uploadUrl, publicUrl } = await generateUrl({
			contentType: file.type,
			fileName:    file.name,
		}).unwrap()
		await new Promise<void>((resolve, reject) => {
			const xhr = new XMLHttpRequest()
			xhr.open('PUT', uploadUrl)
			xhr.setRequestHeader('Content-Type', file.type)
			xhr.onload = () => (xhr.status >= 200 && xhr.status < 300 ? resolve() : reject(new Error(`HTTP ${xhr.status}`)))
			xhr.onerror = () => reject(new Error('Network error'))
			xhr.send(file)
		})
		return publicUrl
	}

	async function handleFilePicked(e: React.ChangeEvent<HTMLInputElement>) {
		const file = e.target.files?.[0]
		e.target.value = ''
		const replacingId = replacingIdRef.current
		replacingIdRef.current = null
		if (!file) return
		setBusy(true)
		setError(null)
		try {
			const publicUrl = await uploadFile(file)
			const created = await addImage({ id: propertyId, body: { url: publicUrl, order: sorted.length } }).unwrap()

			// There is no replace endpoint, so a replacement is add + slot into the
			// old position + delete. Reordering before the delete keeps the new
			// photo where the old one was even if the delete fails.
			if (replacingId) {
				const slot = sorted.findIndex((img) => img.id === replacingId)
				if (slot >= 0) {
					const next = sorted.map((img) => img.id).filter((id) => id !== replacingId)
					next.splice(slot, 0, created.id)
					await reorderImages({ id: propertyId, body: { orderedImageIds: [...next, replacingId] } }).unwrap()
				}
				await removeImage({ id: propertyId, imageId: replacingId }).unwrap()
			}
		} catch (err) {
			console.error('photo upload failed:', err)
			setError(t('properties.card.photos.uploadFailed'))
		} finally {
			setBusy(false)
		}
	}

	async function runOrdering(orderedImageIds: string[]) {
		setError(null)
		try {
			await reorderImages({ id: propertyId, body: { orderedImageIds } }).unwrap()
		} catch (err) {
			console.error('photo reorder failed:', err)
			setError(t('properties.card.photos.reorderFailed'))
		}
	}

	function handleMove(idx: number, dir: -1 | 1) {
		const target = idx + dir
		if (target < 0 || target >= sorted.length) return
		const next = sorted.map((img) => img.id)
		const tmp = next[idx]
		next[idx] = next[target]
		next[target] = tmp
		void runOrdering(next)
	}

	function handleMakeCover(id: string) {
		void runOrdering([id, ...sorted.map((img) => img.id).filter((imgId) => imgId !== id)])
	}

	async function handleRemove(imageId: string) {
		setError(null)
		try {
			await removeImage({ id: propertyId, imageId }).unwrap()
		} catch (err) {
			console.error('photo delete failed:', err)
			setError(t('properties.card.photos.deleteFailed'))
		}
	}

	const iconButton =
		'rounded-md bg-black/50 p-1 text-white transition-colors hover:bg-black/70 disabled:opacity-40'

	return (
		<div className="flex flex-col gap-3">
			<p className="text-sm font-[500] text-(--color-foreground)">{t('properties.card.photos.title')}</p>
			<div className="grid grid-cols-2 gap-2">
				{sorted.map((img, idx) => (
					<div key={img.id} className="flex flex-col gap-1">
						<div className="relative aspect-[4/3] overflow-hidden rounded-lg bg-(--color-muted)">
							<img src={img.url} alt="" className="absolute inset-0 size-full object-cover" />
							{idx === 0 && (
								<span className="absolute top-1 left-1 rounded-md bg-black/50 px-1.5 py-0.5 text-[10px] text-white">
									{t('properties.card.photos.cover')}
								</span>
							)}
							<div className="absolute top-1 right-1 flex gap-1">
								<button
									type="button"
									className={iconButton}
									disabled={busy}
									onClick={() => openPicker(img.id)}
									aria-label={t('properties.card.photos.replace')}
									title={t('properties.card.photos.replace')}
								>
									<RefreshCwIcon className="size-3" />
								</button>
								<button
									type="button"
									className={iconButton}
									onClick={() => setDeleteTargetId(img.id)}
									aria-label={t('properties.card.photos.delete')}
									title={t('properties.card.photos.delete')}
								>
									<Trash2Icon className="size-3" />
								</button>
							</div>
						</div>
						<div className="flex gap-1">
							<button
								type="button"
								disabled={idx === 0}
								onClick={() => handleMove(idx, -1)}
								className="flex flex-1 items-center justify-center rounded border border-(--color-border) py-0.5 text-(--color-muted-foreground) transition-colors hover:bg-(--color-muted) disabled:opacity-30"
								aria-label={t('properties.card.photos.moveLeft')}
							>
								<ChevronLeftIcon className="size-3.5" />
							</button>
							<button
								type="button"
								disabled={idx === 0}
								onClick={() => handleMakeCover(img.id)}
								className="flex flex-1 items-center justify-center rounded border border-(--color-border) py-0.5 text-(--color-muted-foreground) transition-colors hover:bg-(--color-muted) disabled:opacity-30"
								aria-label={t('properties.card.photos.makeCover')}
								title={t('properties.card.photos.makeCover')}
							>
								<StarIcon className="size-3.5" />
							</button>
							<button
								type="button"
								disabled={idx === sorted.length - 1}
								onClick={() => handleMove(idx, 1)}
								className="flex flex-1 items-center justify-center rounded border border-(--color-border) py-0.5 text-(--color-muted-foreground) transition-colors hover:bg-(--color-muted) disabled:opacity-30"
								aria-label={t('properties.card.photos.moveRight')}
							>
								<ChevronRightIcon className="size-3.5" />
							</button>
						</div>
					</div>
				))}
				<button
					type="button"
					disabled={busy}
					onClick={() => openPicker(null)}
					className="flex aspect-[4/3] flex-col items-center justify-center gap-1.5 rounded-lg border-2 border-dashed border-(--color-border) text-(--color-muted-foreground) transition-colors hover:border-(--color-primary)/50 hover:text-(--color-foreground) disabled:opacity-50"
				>
					{busy
						? <Spinner className="size-4" />
						: <PlusIcon className="size-4" />
					}
					<span className="text-xs">{busy ? '' : t('properties.card.photos.add')}</span>
				</button>
			</div>
			{error && <p className="text-xs text-(--color-destructive)">{error}</p>}
			<input
				ref={fileInputRef}
				type="file"
				accept="image/*"
				className="hidden"
				onChange={handleFilePicked}
			/>
		<ConfirmDialog
			open={!!deleteTargetId}
			onOpenChange={(open) => { if (!open) setDeleteTargetId(null) }}
			title={t('properties.card.photos.deleteDialog.title')}
			description={t('properties.card.photos.deleteDialog.description')}
			confirmLabel={t('common.delete')}
			cancelLabel={t('common.cancel')}
			variant="destructive"
			onConfirm={() => {
				const id = deleteTargetId
				setDeleteTargetId(null)
				if (id) void handleRemove(id)
			}}
		/>
		</div>
	)
}
